import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import CButton from "../../components/CButton";
import CDataGrid from "../../components/CDataGrid";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { supabase } from "../../config/supabaseClient";
import { downloadExcelTemplateUtility } from "../../constant/utils/commonUtils";
import { PROGRAM_DURATION_OPTIONS } from "../../constant/constants";

const TEMPLATE_COLUMNS = [
  { headerName: "Employee ID", field: "employee_id" },
  { headerName: "Training Name", field: "training_name" },
  { headerName: "Skills Gap/Requirement", field: "skills_gap" },
  { headerName: "Program Duration", field: "program_duration" },
  { headerName: "Priority", field: "priority_level" },
  { headerName: "Skills Requirement Motivation", field: "skill_requirement_motivation" },
];

const REQUIRED_HEADERS = [
  "Employee ID",
  "Training Name",
  "Skills Gap/Requirement",
  "Program Duration",
  "Priority",
  "Skills Requirement Motivation",
];

const PRIORITY_OPTIONS = ["High", "Medium", "Low"];

const EmployeeTrainingImport = () => {
  const fileInputRef = useRef(null);
  const localUser = JSON.parse(localStorage.getItem("sdfUser") || "{}");
  const companyId = localUser?.company_id;

  const [stats, setStats] = useState({
    total: 0,
    not_processed: 0,
    processed: 0,
    error: 0,
  });
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearDialog, setClearDialog] = useState({ open: false });
  const [activeSection, setActiveSection] = useState(null);
  const [sectionRecords, setSectionRecords] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!companyId) return;
    try {
      const table = "import_employee_training_mapping";
      const countOpts = { count: "exact", head: true };
      const [totalRes, notProcRes, procRes, errRes] = await Promise.all([
        supabase.from(table).select("*", countOpts).eq("company_id", companyId),
        supabase.from(table).select("*", countOpts).eq("company_id", companyId).eq("processed", "not_processed"),
        supabase.from(table).select("*", countOpts).eq("company_id", companyId).eq("processed", "processed"),
        supabase.from(table).select("*", countOpts).eq("company_id", companyId).eq("processed", "error"),
      ]);
      if (totalRes.error) throw totalRes.error;
      if (notProcRes.error) throw notProcRes.error;
      if (procRes.error) throw procRes.error;
      if (errRes.error) throw errRes.error;
      setStats({
        total: totalRes.count ?? 0,
        not_processed: notProcRes.count ?? 0,
        processed: procRes.count ?? 0,
        error: errRes.count ?? 0,
      });
    } catch (err) {
      console.error("Error fetching import stats:", err);
      toast.error(err.message || "Failed to load statistics");
    }
  }, [companyId]);

  const PAGE_SIZE = 1000;

  const fetchSectionRecords = useCallback(async (section) => {
    if (!companyId || !section) return;
    setSectionLoading(true);
    try {
      let query = supabase
        .from("import_employee_training_mapping")
        .select("id, employee_id, training_name, skills_gap, program_duration, priority_level, processed, error_message, log, created_at")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      if (section === "not_processed") query = query.eq("processed", "not_processed");
      else if (section === "processed") query = query.eq("processed", "processed");
      else if (section === "error") query = query.eq("processed", "error");
      const all = [];
      let from = 0;
      let hasMore = true;
      while (hasMore) {
        const to = from + PAGE_SIZE - 1;
        const { data, error } = await query.range(from, to);
        if (error) throw error;
        const chunk = data ?? [];
        all.push(...chunk);
        hasMore = chunk.length === PAGE_SIZE;
        from += PAGE_SIZE;
      }
      setSectionRecords(all);
    } catch (err) {
      console.error("Error fetching section records:", err);
      toast.error(err.message || "Failed to load records");
      setSectionRecords([]);
    } finally {
      setSectionLoading(false);
    }
  }, [companyId]);

  const handleSectionClick = (section) => {
    setActiveSection(section);
    fetchSectionRecords(section);
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (!activeSection) setSectionRecords([]);
  }, [activeSection]);

  const handleDownloadTemplate = async () => {
    try {
      await downloadExcelTemplateUtility({
        fileName: "Employee_Training_Import_Template.xlsx",
        sheetName: "Employee Training Import",
        columns: TEMPLATE_COLUMNS.map((c) => ({ headerName: c.headerName, field: c.field })),
        dropdownConfigs: [
          {
            columnName: "Program Duration",
            options: PROGRAM_DURATION_OPTIONS.map((o) => o.value),
            allowBlank: false,
          },
          {
            columnName: "Priority",
            options: PRIORITY_OPTIONS,
            allowBlank: false,
          },
        ],
      });
      toast.success("Template downloaded.");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template.");
    }
  };

  const normalizeHeader = (h) => (h ?? "").toString().trim();
  const mapRowToDb = (row) => ({
    company_id: companyId,
    employee_id: row["Employee ID"] != null ? String(row["Employee ID"]).trim() : null,
    training_name: row["Training Name"] != null ? String(row["Training Name"]).trim() : null,
    skills_gap: row["Skills Gap/Requirement"] != null ? String(row["Skills Gap/Requirement"]).trim() : null,
    program_duration:
      row["Program Duration"] != null ? String(row["Program Duration"]).trim() : null,
    priority_level: row["Priority"] != null ? String(row["Priority"]).trim() : null,
    skill_requirement_motivation:
      row["Skills Requirement Motivation"] != null
        ? String(row["Skills Requirement Motivation"]).trim()
        : null,
  });

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !companyId) {
      if (!companyId) toast.error("Company context missing.");
      event.target.value = "";
      return;
    }

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      toast.error("Please upload an Excel (.xlsx, .xls) or CSV file.");
      event.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const raw = await file.arrayBuffer();
      const workbook = XLSX.read(raw, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!rows.length || !jsonData.length) {
        toast.error("File has no data rows.");
        event.target.value = "";
        setUploading(false);
        return;
      }

      const headers = rows[0].map(normalizeHeader);
      const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
      if (missing.length) {
        toast.error(`Missing columns: ${missing.join(", ")}`);
        event.target.value = "";
        setUploading(false);
        return;
      }

      const toInsert = jsonData.map(mapRowToDb).filter((r) => r.employee_id && r.training_name);
      if (!toInsert.length) {
        toast.error("No valid rows (Employee ID and Training Name are required).");
        event.target.value = "";
        setUploading(false);
        return;
      }

      const BATCH = 100;
      for (let i = 0; i < toInsert.length; i += BATCH) {
        const chunk = toInsert.slice(i, i + BATCH);
        const { error } = await supabase
          .from("import_employee_training_mapping")
          .insert(chunk);
        if (error) throw error;
      }

      toast.success(`${toInsert.length} row(s) imported.`);
      await fetchStats();
    } catch (err) {
      console.error("Import error:", err);
      toast.error(err.message || "Import failed.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleStartProcess = async () => {
    if (!companyId) {
      toast.error("Company context missing.");
      return;
    }
    setProcessing(true);
    try {
      const { error } = await supabase.rpc("process_employee_training_import", {
        p_company_id: companyId,
      });
      if (error) throw error;
      toast.success("Processing completed.");
      await fetchStats();
      if (activeSection) await fetchSectionRecords(activeSection);
    } catch (err) {
      console.error("Process error:", err);
      toast.error(err.message || "Processing failed.");
    } finally {
      setProcessing(false);
    }
  };

  const handleClearTable = async () => {
    if (!companyId) return;
    setClearing(true);
    try {
      const { error } = await supabase
        .from("import_employee_training_mapping")
        .delete()
        .eq("company_id", companyId);
      if (error) throw error;
      toast.success("Import data cleared for this company.");
      setClearDialog({ open: false });
      setSectionRecords([]);
      setActiveSection(null);
      await fetchStats();
    } catch (err) {
      console.error("Clear error:", err);
      toast.error(err.message || "Failed to clear data.");
    } finally {
      setClearing(false);
    }
  };

  if (!companyId) {
    return (
      <div className="p-6">
        <p className="font-medium 2xl:text-base text-sm 2xl:leading-base leading-sm text-gray-300">
          You need to be in a company context to use this page.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold 2xl:mb-4 md:mb-3 mb-4">
        Employee Training Import
      </h2>

      <div className="border-2 border-blue-500 bg-white rounded-lg p-4 md:mb-6 mb-4 relative">
        <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1">
          Instructions
        </p>
        <div>
          <p className="font-medium mb-2 2xl:text-base text-sm 2xl:leading-base leading-sm text-gray-300">
            Map employee training data in bulk. Download the template using &nbsp;
            <CButton
              className="text-blue-600 underline font-semibold 2xl:!h-auto !h-auto 2xl:!p-0 !p-0 !text-sm !leading-sm"
              onClick={handleDownloadTemplate}
            >
              Click here
            </CButton>
            &nbsp; then fill in Employee ID, Training Name, Skills Gap/Requirement, Program Duration (dropdown), Priority (dropdown), and Skills Requirement Motivation. Upload the file to import; use &quot;Start process&quot; to map rows to training forms and selected trainings.
          </p>
        </div>
      </div>

      <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden md:mb-6 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileUpload}
            />
            <CButton
              className="gradient-btn text-white shadow-none 2xl:!h-[50px] md:!h-[45px] !h-[40px] 2xl:!px-6 md:!px-4 !px-3"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <UploadIcon className="!w-5 !h-5 mr-2" />
              {uploading ? "Uploading…" : "Upload File"}
            </CButton>
            <CButton
              onClick={handleStartProcess}
              disabled={processing || stats.not_processed === 0}
              className="bg-blue-500 hover:bg-blue-700 text-white border border-solid border-blue-500 hover:border-blue-700 2xl:!h-[50px] md:!h-[45px] !h-[40px] 2xl:!px-6 md:!px-4 !px-3"
            >
              <PlayArrowIcon className="!w-5 !h-5 mr-2" />
              {processing ? "Processing…" : "Start process"}
            </CButton>
          </div>
          <CButton
            onClick={() => setClearDialog({ open: true })}
            disabled={clearing || stats.total === 0}
            className="bg-blue-75 text-blue-500 hover:bg-blue-100 border border-solid border-blue-300 rounded-lg 2xl:!h-[50px] md:!h-[45px] !h-[40px] 2xl:!px-6 md:!px-4 !px-3 disabled:opacity-50"
          >
            <DeleteSweepIcon className="!w-5 !h-5 mr-2 text-blue-500" />
            {clearing ? "Clearing…" : "Clear table"}
          </CButton>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 pt-0 border-t border-solid border-blue-100">
          <button
            type="button"
            disabled={stats.total === 0}
            onClick={() => stats.total > 0 && handleSectionClick("all")}
            className={`text-left rounded-lg p-4 border border-solid transition-all disabled:cursor-default ${
              activeSection === "all"
                ? "bg-blue-100 border-blue-500 ring-2 ring-blue-300"
                : "bg-blue-75/50 border-blue-200 hover:bg-blue-100 hover:border-blue-300 disabled:hover:bg-blue-75/50 disabled:hover:border-blue-200"
            }`}
          >
            <p className="text-xs font-medium text-blue-700 uppercase tracking-wide 2xl:text-sm text-gray-600">All</p>
            <p className="2xl:text-2xl text-xl font-semibold text-gray-800 mt-1">{stats.total}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.total === 0 ? "No records" : "Click to view"}</p>
          </button>
          <button
            type="button"
            disabled={stats.not_processed === 0}
            onClick={() => stats.not_processed > 0 && handleSectionClick("not_processed")}
            className={`text-left rounded-lg p-4 border border-solid transition-all disabled:cursor-default ${
              activeSection === "not_processed"
                ? "bg-amber-100 border-amber-500 ring-2 ring-amber-300"
                : "bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300 disabled:hover:bg-amber-50 disabled:hover:border-amber-200"
            }`}
          >
            <p className="text-xs font-medium text-amber-700 uppercase tracking-wide 2xl:text-sm">Not processed</p>
            <p className="2xl:text-2xl text-xl font-semibold text-amber-800 mt-1">{stats.not_processed}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.not_processed === 0 ? "No records" : "Click to view"}</p>
          </button>
          <button
            type="button"
            disabled={stats.processed === 0}
            onClick={() => stats.processed > 0 && handleSectionClick("processed")}
            className={`text-left rounded-lg p-4 border border-solid transition-all disabled:cursor-default ${
              activeSection === "processed"
                ? "bg-green-100 border-green-500 ring-2 ring-green-300"
                : "bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300 disabled:hover:bg-green-50 disabled:hover:border-green-200"
            }`}
          >
            <p className="text-xs font-medium text-green-700 uppercase tracking-wide 2xl:text-sm">Processed</p>
            <p className="2xl:text-2xl text-xl font-semibold text-green-800 mt-1">{stats.processed}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.processed === 0 ? "No records" : "Click to view"}</p>
          </button>
          <button
            type="button"
            disabled={stats.error === 0}
            onClick={() => stats.error > 0 && handleSectionClick("error")}
            className={`text-left rounded-lg p-4 border border-solid transition-all disabled:cursor-default ${
              activeSection === "error"
                ? "bg-red-100 border-red-500 ring-2 ring-red-300"
                : "bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300 disabled:hover:bg-red-50 disabled:hover:border-red-200"
            }`}
          >
            <p className="text-xs font-medium text-red-700 uppercase tracking-wide 2xl:text-sm">Error</p>
            <p className="2xl:text-2xl text-xl font-semibold text-red-800 mt-1">{stats.error}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.error === 0 ? "No records" : "Click to view"}</p>
          </button>
        </div>
      </div>

      {activeSection && (
        <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden md:mb-6 mb-4 flex flex-col 2xl:h-[calc(100vh-600px)] md:h-[calc(100vh-580px)] h-[calc(100vh-560px)] min-h-[400px]">
          <div className="p-4 border-b border-solid border-blue-100 shrink-0">
            <h3 className="text-lg font-semibold text-gray-800 2xl:text-xl">
              {activeSection === "all" && "All records"}
              {activeSection === "not_processed" && "Not processed records"}
              {activeSection === "processed" && "Processed records"}
              {activeSection === "error" && "Error records"}
              {" "}({sectionRecords.length})
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {activeSection === "error" &&
                "Rows that failed during process. Fix the data and re-upload or run process again for unprocessed rows."}
              {activeSection !== "error" && "Click a section above to switch view."}
            </p>
          </div>
          <div className="p-4 flex-1 min-h-0 flex flex-col">
            {sectionLoading ? (
              <div className="flex items-center justify-center flex-1 text-gray-500">Loading…</div>
            ) : (
              <div className="flex-1 min-h-0 flex flex-col">
                <CDataGrid
                  key={`${activeSection}-${sectionRecords.length}`}
                  idKey="id"
                  rows={sectionRecords.map((r) => ({
                    ...r,
                    error_display: r.error_message ?? r.log ?? "—",
                  }))}
                  columns={
                    activeSection === "error"
                      ? [
                          { field: "employee_id", headerName: "Employee ID", minWidth: 120, flex: 0.5 },
                          { field: "training_name", headerName: "Training Name", minWidth: 180, flex: 1 },
                          { field: "skills_gap", headerName: "Skills Gap/Requirement", minWidth: 150, flex: 0.8 },
                          { field: "program_duration", headerName: "Program Duration", minWidth: 120, flex: 0.5 },
                          { field: "priority_level", headerName: "Priority", minWidth: 100, flex: 0.4 },
                          { field: "error_display", headerName: "Error message", minWidth: 220, flex: 1.2 },
                        ]
                      : [
                          { field: "employee_id", headerName: "Employee ID", minWidth: 120, flex: 0.5 },
                          { field: "training_name", headerName: "Training Name", minWidth: 180, flex: 1 },
                          { field: "skills_gap", headerName: "Skills Gap/Requirement", minWidth: 150, flex: 0.8 },
                          { field: "program_duration", headerName: "Program Duration", minWidth: 120, flex: 0.5 },
                          { field: "priority_level", headerName: "Priority", minWidth: 100, flex: 0.4 },
                        ]
                  }
                  initialPageSize={
                    sectionRecords.length > 100
                      ? 100
                      : sectionRecords.length > 50
                        ? 50
                        : sectionRecords.length || 25
                  }
                height="100%"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationDialog
        open={clearDialog.open}
        onClose={() => setClearDialog({ open: false })}
        onConfirm={handleClearTable}
        title="Clear import data"
        description="This will delete all imported rows for this company. This action cannot be undone."
        confirmText="Clear"
        cancelText="Cancel"
        confirmClass="bg-red-600 hover:bg-red-700 text-white border border-red-600 px-4 py-2 rounded-lg"
        cancelClass="bg-white text-blue-500 hover:text-white hover:bg-blue-500 border border-blue-500 px-4 py-2 rounded-lg"
        loading={clearing}
      />
    </div>
  );
};

export default EmployeeTrainingImport;
